import type { BenchmarkArm, BenchmarkEventEnvelope } from './types';

// Mirrors twg-api/src/interactors/benchmark/benchmark-transcript.ts (v1).
// Update both contracts and their replay fixtures together. Unknown versions must
// leave lifecycle/accounting events operational; incomplete groups are never rendered.
// Browser transcripts stay in bounded memory, not web storage.
export const TRANSCRIPT_VERSION = 1;
export const MAX_TRANSCRIPT_PENDING_BYTES: number = 256 * 1024;
export const MAX_TRANSCRIPT_CAPTURE_BYTES: number = 14 * 1024 * 1024;
export const MAX_TRANSCRIPT_ITEMS = 4096;
export type TranscriptKind =
	| 'session'
	| 'user'
	| 'assistant'
	| 'reasoning_summary'
	| 'command'
	| 'mcp'
	| 'plan'
	| 'notice'
	| 'turn';
export type TranscriptOperation = 'started' | 'append' | 'replace' | 'completed';
export type TranscriptField = 'text' | 'output' | 'summary' | 'progress';
export type TranscriptFrame = BenchmarkEventEnvelope & {
	version: number;
	arm: BenchmarkArm;
	itemId: string;
	kind: TranscriptKind;
	operation: TranscriptOperation;
	field?: TranscriptField;
	summaryIndex?: number;
	occurredAt: string;
	groupId: string;
	chunkIndex: number;
	chunkCount: number;
	encoding: 'json-fragment';
	content: string;
	truncated: boolean;
};
export type TranscriptPayload = {
	text?: string;
	summary?: readonly string[];
	phase?: 'commentary' | 'final_answer' | null;
	command?: string;
	commandActions?: readonly unknown[];
	output?: string | null;
	status?: string;
	exitCode?: number | null;
	durationMs?: number | null;
	server?: string;
	tool?: string;
	arguments?: unknown;
	result?: unknown;
	error?: unknown;
	explanation?: string | null;
	steps?: readonly { step: string; status: string }[];
	startedAt?: number | null;
	completedAt?: number | null;
	codexVersion?: string;
	model?: string;
	effectiveEffort?: string;
	summarySetting?: string;
};
export type TranscriptRecord = { frame: TranscriptFrame; payload: TranscriptPayload };
export type TranscriptBuffer = { frame: TranscriptFrame; chunks: readonly string[]; bytes: number };
export type TranscriptReadResult =
	| { kind: 'pending'; buffer: TranscriptBuffer }
	| { kind: 'record'; record: TranscriptRecord }
	| { kind: 'invalid' | 'unsupported' };

const record = (value: unknown): value is Record<string, unknown> =>
	value !== null && typeof value === 'object' && !Array.isArray(value);
const kinds: readonly string[] = [
	'session',
	'user',
	'assistant',
	'reasoning_summary',
	'command',
	'mcp',
	'plan',
	'notice',
	'turn',
];
const operations: readonly string[] = ['started', 'append', 'replace', 'completed'];
const fields: readonly string[] = ['text', 'output', 'summary', 'progress'];
export const transcriptBytes = (text: string): number => new TextEncoder().encode(text).byteLength;

// Only allowlisted presentation fields leave this parser. Internal reasoning and
// arbitrary HTML/binary fields cannot become renderable items.
function payloadFor(frame: TranscriptFrame, value: unknown): TranscriptPayload | null {
	if (!record(value)) return null;
	if (frame.operation === 'append')
		return typeof value.text === 'string' ? { text: value.text } : null;
	const payload: TranscriptPayload = {};
	const strings = [
		'text',
		'command',
		'status',
		'server',
		'tool',
		'codexVersion',
		'model',
		'effectiveEffort',
	] as const;
	for (const key of strings) {
		if (value[key] !== undefined) {
			if (typeof value[key] !== 'string') return null;
			payload[key] = value[key];
		}
	}
	for (const key of ['output', 'explanation'] as const) {
		if (value[key] !== undefined) {
			if (value[key] !== null && typeof value[key] !== 'string') return null;
			payload[key] = value[key];
		}
	}
	for (const key of ['exitCode', 'durationMs', 'startedAt', 'completedAt'] as const) {
		if (value[key] !== undefined) {
			if (value[key] !== null && (typeof value[key] !== 'number' || !Number.isFinite(value[key])))
				return null;
			payload[key] = value[key];
		}
	}
	if (frame.kind === 'session') {
		if (typeof value.codexVersion !== 'string' || typeof value.model !== 'string') return null;
		if (typeof value.summary === 'string') payload.summarySetting = value.summary;
	} else if (value.summary !== undefined) {
		if (
			!Array.isArray(value.summary) ||
			value.summary.length > MAX_TRANSCRIPT_ITEMS ||
			!value.summary.every((entry) => typeof entry === 'string')
		)
			return null;
		payload.summary = value.summary;
	}
	if (value.phase !== undefined) {
		if (value.phase !== null && value.phase !== 'commentary' && value.phase !== 'final_answer')
			return null;
		payload.phase = value.phase;
	}
	if (value.steps !== undefined) {
		if (
			!Array.isArray(value.steps) ||
			!value.steps.every(
				(step) =>
					record(step) &&
					typeof step.step === 'string' &&
					['pending', 'inProgress', 'completed'].includes(String(step.status)),
			)
		)
			return null;
		payload.steps = value.steps.map((step) => ({ step: step.step, status: step.status }));
	}
	if (Array.isArray(value.commandActions)) payload.commandActions = value.commandActions;
	if (frame.kind === 'mcp')
		for (const key of ['arguments', 'result', 'error'] as const)
			if (value[key] !== undefined) payload[key] = value[key];
	return payload;
}

function isTimestamp(value: unknown): boolean {
	return typeof value === 'string' && Number.isFinite(Date.parse(value));
}

function isBoundedIdentifier(value: unknown, maximumLength: number): boolean {
	return typeof value === 'string' && value.length > 0 && value.length <= maximumLength;
}

function hasValidEnvelope(frame: TranscriptFrame): boolean {
	return (
		['control', 'twg'].includes(frame.arm) &&
		typeof frame.runId === 'string' &&
		Number.isSafeInteger(frame.sequence) &&
		frame.sequence >= 1 &&
		isTimestamp(frame.timestamp) &&
		isTimestamp(frame.occurredAt) &&
		isBoundedIdentifier(frame.itemId, 256) &&
		isBoundedIdentifier(frame.groupId, 128) &&
		kinds.includes(frame.kind) &&
		operations.includes(frame.operation)
	);
}

function hasValidChunkMetadata(frame: TranscriptFrame): boolean {
	return !(
		frame.encoding !== 'json-fragment' ||
		typeof frame.content !== 'string' ||
		typeof frame.truncated !== 'boolean' ||
		transcriptBytes(frame.content) > 24 * 1024 ||
		!Number.isInteger(frame.chunkCount) ||
		frame.chunkCount < 1 ||
		frame.chunkCount > 1024 ||
		!Number.isInteger(frame.chunkIndex) ||
		frame.chunkIndex < 0 ||
		frame.chunkIndex >= frame.chunkCount
	);
}

function hasValidAppendMetadata(frame: TranscriptFrame): boolean {
	return !(
		(frame.field !== undefined && !fields.includes(frame.field)) ||
		(frame.operation === 'append' && frame.field === undefined) ||
		(frame.field === 'summary' &&
			(!Number.isInteger(frame.summaryIndex) ||
				Number(frame.summaryIndex) < 0 ||
				Number(frame.summaryIndex) >= MAX_TRANSCRIPT_ITEMS))
	);
}

function matchesPendingFrame(frame: TranscriptFrame, pending: TranscriptBuffer): boolean {
	const identityFields = [
		'groupId',
		'runId',
		'arm',
		'itemId',
		'kind',
		'operation',
		'field',
		'summaryIndex',
		'chunkCount',
		'occurredAt',
		'truncated',
	] as const;
	return (
		identityFields.every((key) => pending.frame[key] === frame[key]) &&
		pending.frame.sequence + pending.chunks.length === frame.sequence
	);
}

export function readTranscriptFrame(
	input: unknown,
	pending: TranscriptBuffer | undefined,
): TranscriptReadResult {
	if (!record(input)) return { kind: 'invalid' };
	if (input.version !== TRANSCRIPT_VERSION) return { kind: 'unsupported' };
	const frame = input as TranscriptFrame;
	if (!hasValidEnvelope(frame) || !hasValidChunkMetadata(frame) || !hasValidAppendMetadata(frame))
		return { kind: 'invalid' };
	if (pending && !matchesPendingFrame(frame, pending)) return { kind: 'invalid' };
	if (frame.chunkIndex !== (pending?.chunks.length ?? 0)) return { kind: 'invalid' };
	const bytes = (pending?.bytes ?? 0) + transcriptBytes(frame.content);
	if (bytes > MAX_TRANSCRIPT_PENDING_BYTES) return { kind: 'invalid' };
	const chunks = [...(pending?.chunks ?? []), frame.content];
	if (chunks.length < frame.chunkCount)
		return { kind: 'pending', buffer: { frame: pending?.frame ?? frame, chunks, bytes } };
	try {
		const payload = payloadFor(frame, JSON.parse(chunks.join('')));
		return payload ? { kind: 'record', record: { frame, payload } } : { kind: 'invalid' };
	} catch {
		return { kind: 'invalid' };
	}
}
